import { useState } from "react";
import moment from "moment";
import { Box } from "@mui/material";
import { DataTable, DataRow, StatusPill, TD_STYLE, MONO_STYLE } from "../../dashboard/components/DashboardShared";
import LessMoreComp from "../../../shared_elements/components/CustomLessMoreComp";
import { dateMonthYearDateFormat } from "../../../constants";
import "../styles/analytics.scss";

function getValueByPath(obj, keyPath) {
  if (!obj || typeof obj !== "object") return undefined;
  if (!keyPath || typeof keyPath !== "string") return undefined;
  return keyPath.split(".").reduce((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return acc[key];
  }, obj);
}

function formatDateValue(value) {
  if (value == null || value === "") return "-";
  const date = moment(value);
  if (!date.isValid()) return String(value);
  return date.format(dateMonthYearDateFormat);
}

function isDescriptionColumn(col) {
  return String(col?.key || col?.label || "").trim().toLowerCase() === "description";
}

const RECIPIENTS_VISIBLE_COUNT = 2;

function isRecipientsColumn(col) {
  const label = String(col?.key || col?.label || "").trim().toLowerCase();
  return label === "internal recipients" || label === "external recipients";
}

function RecipientsCell({ value }) {
  const [expanded, setExpanded] = useState(false);
  const emails = value.split(",").map((email) => email.trim()).filter(Boolean);
  if (emails.length <= RECIPIENTS_VISIBLE_COUNT) return value;
  const visibleEmails = expanded ? emails : emails.slice(0, RECIPIENTS_VISIBLE_COUNT);
  return (
    <>
      {visibleEmails.join(", ")}{" "}
      <Box
        component="span"
        onClick={(event) => { event.stopPropagation(); setExpanded((prev) => !prev); }}
        sx={{ color: "#0e7fe1", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {expanded ? "..less" : `+${emails.length - RECIPIENTS_VISIBLE_COUNT} more`}
      </Box>
    </>
  );
}

function renderCellValue(col, row, rowIndex) {
  const raw = getValueByPath(row, col.key);
  if (col.type === "pill" && raw && typeof raw === "object") {
    return <StatusPill label={raw.label || "-"} type={raw.tone || "navy"} style={raw.style} />;
  }
  if (col.type === "pill") {
    const fallbackType =
      row?.[`${col.key}Type`] ||
      row?.[`${col.key}_type`] ||
      row?.statusType ||
      "navy";
    return <StatusPill label={raw == null || raw === "" ? "-" : String(raw)} type={fallbackType} />;
  }
  if (col.type === "date") {
    return formatDateValue(raw);
  }
  if (raw == null || raw === "") return "-";
  if (isDescriptionColumn(col)) {
    return <LessMoreComp name={String(raw)} lessMr={`description-${row?.id ?? row?.uuid ?? rowIndex}`} />;
  }
  if (isRecipientsColumn(col)) {
    return <RecipientsCell value={String(raw)} />;
  }
  return String(raw);
}

const CENTER_ALIGNED_COLUMN_LABELS = ["leases", "amount", "% total"];

function isCenterAlignedColumn(col) {
  return CENTER_ALIGNED_COLUMN_LABELS.includes(String(col?.label || "").trim().toLowerCase());
}

function getDeltaColor(value) {
  const text = value == null ? "" : String(value).trim();
  if (!text) return undefined;
  const numeric = parseFloat(text.replace(/[^0-9.\-]/g, ""));
  if (isNaN(numeric) || numeric === 0) return undefined;
  if (text.startsWith("+")) return "#22863a";
  if (text.startsWith("-")) return "#C0392B";
  return undefined;
}

function getCellStyle(col, row) {
  const style = col.mono ? { ...TD_STYLE, ...MONO_STYLE } : { ...TD_STYLE };
  if (isCenterAlignedColumn(col)) {
    style.textAlign = "center";
  }
  const raw = getValueByPath(row, col.key);
  const isUpliftColumn =
    String(col.key || "").toLowerCase() === "uplift" ||
    String(col.type || "").toLowerCase() === "uplift";
  if (isUpliftColumn) {
    const deltaColor = getDeltaColor(raw);
    if (deltaColor) style.color = deltaColor;
  }
  return style;
}

const GraphTable = ({ columns, rows, maxHeight, onRowClick }) => {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const hasRows = safeRows.length > 0;

  return (
    <Box data-table-scroll-root="true" className="graph-table-scroll-root" sx={maxHeight != null ? { maxHeight } : undefined} >
      <DataTable
        columns={safeColumns.map((col) =>
          isCenterAlignedColumn(col)
            ? { label: col.label || col.key || "-", align: "center" }
            : col.label || col.key || "-",
        )}
      >
        {hasRows ? (
          safeRows.map((row, rowIndex) => (
            <DataRow key={row.id ?? row.uuid ?? rowIndex} highlight={row?.rowHighlight || row?.row_highlight || null} onClick={onRowClick ? () => onRowClick(row) : undefined} >
              {safeColumns.map((col, colIndex) => (
                <td key={`${col.key || colIndex}-${rowIndex}`} style={getCellStyle(col, row)} >
                  {renderCellValue(col, row, rowIndex)}
                </td>
              ))}
            </DataRow>
          ))
        ) : (
          <DataRow>
            <td colSpan={Math.max(1, safeColumns.length)} style={{ ...TD_STYLE, textAlign: "center", color: "#8E97A8", padding: "18px 12px", }} >
              No data available
            </td>
          </DataRow>
        )}
      </DataTable>
    </Box>
  );
};

export default GraphTable;